
import * as React from 'react';
import * as style from './gift-description.less';
import { PaymentLabel, FrequencyCode } from '../../webgiving-generated';
import { Formatter } from '../../../Shared/utils/formatter';
import { Fragment } from '../../../Shared/components/fragment';
import { dateOfFirstGiftForDisplay } from '../../utils/recurring-utils';
import { dateOfGiftForDisplay } from '../../utils/onetime-gift-utils';
import { Text } from '../../components/text';

export interface IGiftCompleteProps {
	isRecurring: boolean;
	displayRecurringSuggestion: boolean;
	fundName?: string;
	merchantCurrency: string;
	giftAmount: number;
	receiptSent: boolean;
	emailAddress: string;
	paymentLabel: PaymentLabel;
	merchantName: string;
	stillProcessing: boolean;
	wasDelayed: boolean;
	startDate?: Date;
	frequency?: FrequencyCode;
	isOneTimeFutureGift: boolean;
	oneTimeGiftDate: Date | null;
	thankYouText?: string;
}

const DelayedGiftCompleteCopy = (props: IGiftCompleteProps) => {
	const { paymentLabel: { NounLowerCase }, merchantCurrency, merchantName, giftAmount, emailAddress, isRecurring, thankYouText } = props;

	if (NewFeatures.SunsetFastpay && !!thankYouText) {
		return [
			<h1 key="title"><Text>Thank you! We've received your {NounLowerCase}.</Text></h1>,
			<p className="lead" data-pp-at-target={isRecurring ? 'Recurring Success Message' : 'One-time Success Message'}>
				<Text>{thankYouText}</Text></p>,
		] as any as JSX.Element;
	}

	return [
		<h1 key="title"><Text>Thank you! We've received your {NounLowerCase}.</Text></h1>,
		<p className="lead" data-pp-at-target={isRecurring ? 'Recurring Success Message' : 'One-time Success Message'}>
			<Text>You have authorized a {NounLowerCase} of</Text> <strong className={style.currencyCode}>{merchantCurrency}</strong>
			&nbsp;<strong>${Formatter.formatNumberForDisplay(giftAmount, 2)}</strong> <Text>to</Text> {merchantName}.
			<Text params={{ emailAddress}}>{` We'll send an email to {{emailAddress}} when the ${NounLowerCase} has been processed.`}</Text>
		</p>,
	] as any as JSX.Element;
};

interface IGiftCompleteCopyProps extends IGiftCompleteProps {
	title: string | JSX.Element;
	secondPart: string | JSX.Element;
	recurringSuggestion?: () => JSX.Element;
	statusText: string;
}

const GiftCompleteCopy = (props: IGiftCompleteCopyProps) => {

	const {
		title,
		secondPart,
		recurringSuggestion,
		displayRecurringSuggestion,
		fundName,
		isRecurring,
		merchantName,
		merchantCurrency,
		giftAmount,
		paymentLabel: {
			NounLowerCase,
		},
		statusText,
		thankYouText
	} = props;

	const fundLabel = fundName && ` towards {{fundName}}`;

	if (NewFeatures.SunsetFastpay && !!thankYouText) {
		return [
			<h1 key="title">{title}</h1>,
			<p className="lead"
				key="lead"
				data-pp-at-target={isRecurring ? 'Recurring Success Message' : 'One-time Success Message'}
			><Text>{thankYouText}</Text></p>,
		] as any as JSX.Element;
	}

	return [
		<h1 key="title">{title}</h1>,
		<p className="lead"
			key="lead"
			data-pp-at-target={isRecurring ? 'Recurring Success Message' : 'One-time Success Message'}
		><Text>Your {isRecurring ? 'recurring ' : ''}
			{NounLowerCase} of</Text> <strong className={style.currencyCode}>{merchantCurrency}</strong> <strong>${Formatter.formatNumberForDisplay(giftAmount, 2)}</strong>{fundLabel && <Text params={{fundName}}>{fundLabel}</Text>}
			<Text> for </Text><Text params={{merchantName}}>{` {{merchantName}} ${statusText}. `}</Text>{secondPart ? secondPart : ''}
			{!!displayRecurringSuggestion && recurringSuggestion && recurringSuggestion()}
		</p>,
	] as any as JSX.Element;
};

export class GiftComplete extends React.Component<IGiftCompleteProps> {
	render() {

		const {
			isRecurring,
			isOneTimeFutureGift,
			oneTimeGiftDate,
			receiptSent,
			emailAddress,
			stillProcessing,
			wasDelayed,
			startDate,
			frequency,
			displayRecurringSuggestion,
			paymentLabel: {
				NounLowerCase,
			},
		} = this.props;

		let title: string | JSX.Element = <Text>Thank you for your generosity!</Text>;
		let secondPart: string | JSX.Element = '';

		if (wasDelayed) {
			return (<DelayedGiftCompleteCopy  {...this.props} />);
		} else if (stillProcessing) {
			title = <Text>Thank you. Your {NounLowerCase} is still processing.</Text>;
			secondPart = <Text params={{ emailAddress }}>{`You'll receive a ${NounLowerCase} receipt to your email address {{emailAddress}} when the ${NounLowerCase} is processed.`}</Text>;
			status = 'is processing';
		} else if (isRecurring && !receiptSent && startDate && frequency) {
			const startDateFormatted = dateOfFirstGiftForDisplay(frequency, startDate);
			status = 'has been set up';
			secondPart = <Text params={{ date: startDateFormatted }}>{`The first ${NounLowerCase} will be made on {{date}}.`}</Text>;
		} else if (isOneTimeFutureGift && oneTimeGiftDate) {
			secondPart = <Text params={{date: dateOfGiftForDisplay(oneTimeGiftDate), emailAddress}}>{`The donation will be processed on {{date}} and a gift receipt will be sent to your email address {{emailAddress}}.`}</Text>;
			status = 'was scheduled successfully';
		} else {
			secondPart = <Text params={{ emailAddress }}>{`We've sent a ${NounLowerCase} receipt to your email address {{emailAddress}}.`}</Text>;
			status = 'was successful';
		}

		return (<GiftCompleteCopy title={title} secondPart={secondPart} recurringSuggestion={this.getSuggestion}
			displayRecurringSuggestion={displayRecurringSuggestion}
			statusText={status} {...this.props} />);
	}

	private getSuggestion = () => (
		<Fragment>
			<br/><br/>
			<Text>Do you want to set up a recurring {this.props.paymentLabel.NounLowerCase}?</Text>
		</Fragment>
	)
}
